// SM-367 / SM-386: meinUnterricht B2C subscriptions run a fixed 12-month
// committed term. The customer-facing "end of subscription" / "Nächstes
// Verlängerungsdatum" is that term end (start + 12 months), NOT Stripe's
// `current_period_end` — which, on a monthly-billed sub, is only the next
// monthly billing date (a 1-month value that reads as the whole subscription
// ending next month).
export const SUBSCRIPTION_TERM_MONTHS = 12;

// The fixed anchor the term end is measured from. Precedence mirrors
// `computeCommitmentEnd` in `@kimuno/billing` (`(trial_end ?? start_date) + 12mo`),
// so the FE-computed term end matches the SM-213 confirmation email exactly:
//   1. `trialEndAt` — a sub that trialed (or is trialing) anchors its committed
//      term on trial end, not signup date. Stripe keeps `trial_end` set after
//      conversion, so the anchor stays fixed across the trial→paid boundary.
//   2. `startedAt` — SM-367 added the fixed `start_date` mirror, used when there
//      was no trial. Not yet on the api-sdk `SubscriptionDetailsResponse` type or
//      the legacy proxy passthrough, so it's read structurally and is `undefined`
//      at runtime until both ship — remove the `periodStart` fallback then.
//   3. `periodStart` — pre-SM-367 fallback. Rolls forward on every renewal so it
//      drifts on monthly-billed subs, but it's the safest interim value while
//      `startedAt` is en route to the FE.
export type TermAnchorSource =
	| {
			trialEndAt?: string | null;
			startedAt?: string;
			periodStart?: string;
	  }
	| null
	| undefined;

export const resolveTermAnchor = (subscription: TermAnchorSource): string | null =>
	subscription?.trialEndAt ?? subscription?.startedAt ?? subscription?.periodStart ?? null;

// Term end = the anchor's UTC calendar date + 12 months. Derived and formatted
// in UTC so the displayed day doesn't shift back in negative-offset timezones.
// `Date.UTC` normalizes the month overflow (month + 12) into the next year.
export const computeTermEndDate = (anchor: string): Date => {
	const start = new Date(anchor);
	return new Date(
		Date.UTC(
			start.getUTCFullYear(),
			start.getUTCMonth() + SUBSCRIPTION_TERM_MONTHS,
			start.getUTCDate()
		)
	);
};

const formatUtcDate = (date: Date): string =>
	date.toLocaleDateString('de-DE', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		timeZone: 'UTC',
	});

export const formatTermEnd = (anchor: string): string => formatUtcDate(computeTermEndDate(anchor));

// The raw-date sibling of `formatTermEnd`: formats an ISO date as-is (no +12
// months). SM-421 uses it for the monthly upgrade summary, where the next
// payment falls on Stripe's `current_period_end`, not the committed term end.
export const formatIsoDate = (iso: string): string => formatUtcDate(new Date(iso));
